import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Phone,
  Star,
  ShieldCheck,
  Clock,
  Award,
  MapPinned,
  Sparkles,
  Check,
  Droplets,
  Flame,
  Hammer,
  Building2,
} from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { ServiceCard } from "../components/ServiceCard";
import { StatNumber } from "../components/StatNumber";
import { SERVICE_KEYS, SERVICE_META, TESTIMONIALS, CONTACT_INFO } from "../lib/data";
import { buildQuoteMailto } from "../lib/mailto";

const SERVICE_ICONS = { Droplets, Flame, Hammer, Building2 };

export default function Home() {
  const { t, lang } = useLang();
  const quoteMailto = buildQuoteMailto(lang);

  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#F8FAFC] to-white">
        <div className="absolute inset-0 grain-texture pointer-events-none" />
        {/* Decorative shapes */}
        <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-[#4D6BFF]/10 blur-3xl" />
        <div className="absolute bottom-0 -left-24 h-72 w-72 rounded-full bg-sky-200/40 blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 lg:pt-24 pb-20 lg:pb-28">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-7 space-y-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 px-4 py-1.5 text-xs font-semibold text-[#0022FF] shadow-sm"
              >
                <Sparkles className="h-3.5 w-3.5" />
                <span>{t.home.heroEyebrow}</span>
                <span className="text-slate-400">·</span>
                <span className="text-slate-500">{t.home.heroBadge}</span>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.1 }}
                className="font-display font-black text-slate-900 text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight"
              >
                {t.home.heroTitle.split(".")[0]}.
                <br />
                <span className="text-[#0022FF]">{t.home.heroTitle.split(".")[1]}.</span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.2 }}
                className="text-lg text-slate-600 leading-relaxed max-w-xl"
              >
                {t.home.heroSubtitle}
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.3 }}
                className="flex flex-wrap gap-3"
              >
                <a
                  href={quoteMailto}
                  data-testid="hero-cta-quote"
                  className="inline-flex items-center gap-2 rounded-full btn-shk-primary px-7 py-3.5 text-sm font-semibold"
                >
                  {t.home.heroCta1}
                  <ArrowRight className="h-4 w-4" />
                </a>
                <Link
                  to="/dienstleistungen"
                  data-testid="hero-cta-services"
                  className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-7 py-3.5 text-sm font-semibold text-slate-800 hover:border-[#0022FF] hover:text-[#0022FF] transition-colors"
                >
                  {t.home.heroCta2}
                </Link>
              </motion.div>

              {/* Trust row */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
                className="flex flex-wrap items-center gap-6 pt-6"
              >
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <div className="flex -space-x-2">
                    {[1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className="h-8 w-8 rounded-full bg-gradient-to-br from-sky-200 to-sky-400 border-2 border-white"
                      />
                    ))}
                  </div>
                  <span className="font-semibold">850+</span>
                  <span className="text-slate-500">happy clients</span>
                </div>
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Star key={i} className="h-4 w-4 fill-amber-400 text-amber-400" />
                  ))}
                  <span className="ml-1 text-sm font-semibold text-slate-700">5.0</span>
                </div>
              </motion.div>
            </div>

            {/* Hero visual */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="lg:col-span-5 relative"
            >
              <div className="relative aspect-[4/5] rounded-[2.5rem] overflow-hidden shadow-2xl">
                <img
                  src="https://images.pexels.com/photos/8092/pexels-photo.jpg?auto=compress&cs=tinysrgb&w=900&h=1100&fit=crop"
                  alt="SHK Haustechnik"
                  className="h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0022FF]/30 via-transparent to-transparent" />
              </div>
              {/* Floating cards */}
              <motion.div
                animate={{ y: [0, -12, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -bottom-6 -left-6 lg:-left-12 bg-white rounded-2xl shadow-xl border border-slate-200 p-4 flex items-center gap-3 max-w-[260px]"
              >
                <div className="h-12 w-12 rounded-xl bg-rose-100 flex items-center justify-center">
                  <Clock className="h-6 w-6 text-rose-600" />
                </div>
                <div>
                  <div className="font-display font-black text-slate-900 text-lg leading-none">24/7</div>
                  <div className="text-xs text-slate-500">{t.footer.pikett}</div>
                </div>
              </motion.div>
              <motion.div
                animate={{ y: [0, 10, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                className="absolute -top-6 -right-4 lg:-right-8 bg-white rounded-2xl shadow-xl border border-slate-200 p-4 flex items-center gap-3"
              >
                <div className="h-12 w-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <ShieldCheck className="h-6 w-6 text-emerald-600" />
                </div>
                <div>
                  <div className="font-display font-black text-slate-900 text-lg leading-none">98%</div>
                  <div className="text-xs text-slate-500">{t.home.statSatisfaction}</div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#0022FF] mb-4">
                <span className="h-px w-8 bg-[#0022FF]" />
                {t.nav.services}
              </div>
              <h2 className="font-display font-black text-slate-900 text-4xl sm:text-5xl tracking-tight">
                {t.home.servicesTitle}
              </h2>
              <p className="mt-4 text-lg text-slate-600">{t.home.servicesSubtitle}</p>
            </div>
            <Link
              to="/dienstleistungen"
              data-testid="home-services-all"
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#0022FF] hover:underline"
            >
              {t.services.allServicesTitle}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {SERVICE_KEYS.map((k, i) => (
              <ServiceCard key={k} slug={k} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* SERVICE SPOTLIGHTS — every service shown in detail */}
      <section className="bg-slate-50 py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-16">
            <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#0022FF] mb-4">
              <span className="h-px w-8 bg-[#0022FF]" />
              Im Detail
            </div>
            <h2 className="font-display font-black text-slate-900 text-4xl sm:text-5xl tracking-tight">
              Jede Dienstleistung. Im Detail.
            </h2>
          </div>

          <div className="space-y-24 lg:space-y-32">
            {SERVICE_KEYS.map((k, i) => {
              const meta = SERVICE_META[k];
              const svc = t.services[k];
              const Icon = SERVICE_ICONS[meta.icon];
              const reverse = i % 2 === 1;
              return (
                <motion.div
                  key={k}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                  className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center"
                  data-testid={`home-spotlight-${k}`}
                >
                  <div className={`lg:col-span-6 ${reverse ? "lg:order-2" : ""}`}>
                    <div className="relative">
                      <div className="relative aspect-[4/3] rounded-[2rem] overflow-hidden shadow-2xl">
                        <img
                          src={meta.image}
                          alt={svc.name}
                          className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0022FF]/40 via-transparent to-transparent" />
                      </div>
                      <div
                        className="absolute -top-5 -left-5 lg:-left-8 h-20 w-20 rounded-2xl flex items-center justify-center shadow-xl bg-white"
                        style={{ color: meta.color }}
                      >
                        <Icon className="h-10 w-10" strokeWidth={2} />
                      </div>
                      <div className="absolute bottom-5 right-5 inline-flex items-center gap-2 rounded-full bg-white/95 backdrop-blur px-4 py-2 shadow-lg">
                        <span className="font-display font-black text-[#0022FF] text-lg">
                          0{i + 1}
                        </span>
                        <span className="text-xs uppercase tracking-wider font-semibold text-slate-600">
                          / 04
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className={`lg:col-span-6 ${reverse ? "lg:order-1" : ""}`}>
                    <div
                      className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.2em] font-bold mb-4"
                      style={{ backgroundColor: `${meta.color}15`, color: meta.color }}
                    >
                      0{i + 1} · {svc.name}
                    </div>
                    <h3 className="font-display font-black text-slate-900 text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.1]">
                      {svc.short}
                    </h3>
                    <p className="mt-5 text-slate-600 leading-relaxed text-lg">{svc.long}</p>

                    <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {svc.benefits.map((b, j) => (
                        <li
                          key={j}
                          className="flex items-start gap-2.5 rounded-xl bg-white border border-slate-200 px-4 py-3"
                        >
                          <div className="h-5 w-5 rounded-md bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0 mt-0.5">
                            <Check className="h-3 w-3" strokeWidth={3} />
                          </div>
                          <span className="text-sm font-medium text-slate-700">{b}</span>
                        </li>
                      ))}
                    </ul>

                    <div className="mt-8 flex flex-wrap gap-3">
                      <Link
                        to={`/dienstleistungen/${k}`}
                        data-testid={`home-spotlight-cta-${k}`}
                        className="inline-flex items-center gap-2 rounded-full btn-shk-primary px-6 py-3 text-sm font-semibold"
                      >
                        {t.home.servicesLearnMore}
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                      <Link
                        to="/kontakt"
                        className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-6 py-3 text-sm font-semibold text-slate-800 hover:border-[#0022FF] hover:text-[#0022FF] transition"
                      >
                        {t.services.ctaQuote}
                      </Link>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-[#0022FF] text-white py-20 lg:py-24 relative overflow-hidden">
        <div className="absolute inset-0 grain-texture opacity-50" />
        <div className="absolute -top-1/2 -right-1/4 h-[600px] w-[600px] rounded-full bg-sky-400/10 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tight">
              {t.home.statsTitle}
            </h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
            {[
              { value: 15, suffix: "+", label: t.home.statYears, icon: Award },
              { value: 850, suffix: "+", label: t.home.statProjects, icon: ShieldCheck },
              { value: 98, suffix: "%", label: t.home.statSatisfaction, icon: Star },
              { value: 24, suffix: "/7", label: t.home.statResponse, icon: Clock },
            ].map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.6 }}
                className="text-center"
                data-testid={`stat-${i}`}
              >
                <s.icon className="h-8 w-8 mx-auto mb-3 text-sky-300" />
                <div className="text-5xl sm:text-6xl lg:text-7xl font-display font-black">
                  <StatNumber value={s.value} suffix={s.suffix} />
                </div>
                <div className="mt-2 text-sm text-sky-200/80 font-medium">{s.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-12">
            <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#0022FF] mb-4">
              <span className="h-px w-8 bg-[#0022FF]" />
              Why us
            </div>
            <h2 className="font-display font-black text-slate-900 text-4xl sm:text-5xl tracking-tight">
              {t.home.whyTitle}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Award, title: t.home.why1Title, text: t.home.why1Text },
              { icon: Clock, title: t.home.why2Title, text: t.home.why2Text },
              { icon: ShieldCheck, title: t.home.why3Title, text: t.home.why3Text },
              { icon: MapPinned, title: t.home.why4Title, text: t.home.why4Text },
            ].map((w, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.5 }}
                className="rounded-2xl border border-slate-200 bg-white p-6 hover:border-[#0022FF] hover:shadow-lg transition-all"
              >
                <div className="h-12 w-12 rounded-xl bg-[#0022FF]/10 text-[#0022FF] flex items-center justify-center mb-4">
                  <w.icon className="h-6 w-6" />
                </div>
                <h3 className="font-display font-bold text-lg text-slate-900 mb-2">{w.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{w.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20 lg:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="font-display font-black text-slate-900 text-4xl sm:text-5xl tracking-tight">
              {t.home.testimonialsTitle}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((tm, i) => (
              <motion.div
                key={tm.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.6 }}
                className="rounded-3xl border border-slate-200 bg-white p-8 hover:shadow-xl transition-shadow"
                data-testid={`testimonial-${tm.id}`}
              >
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(tm.rating)].map((_, j) => (
                    <Star key={j} className="h-4 w-4 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <p className="text-slate-700 leading-relaxed mb-6 italic">&ldquo;{tm.text[lang]}&rdquo;</p>
                <div className="pt-4 border-t border-slate-100">
                  <div className="font-semibold text-slate-900">{tm.name}</div>
                  <div className="text-xs text-slate-500">{tm.role[lang]}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative overflow-hidden rounded-[2.5rem] bg-[#0022FF] text-white p-10 md:p-16 lg:p-20"
          >
            <div className="absolute inset-0 grain-texture opacity-40" />
            <div className="absolute -top-20 -right-20 h-80 w-80 rounded-full bg-sky-400/20 blur-3xl" />
            <div className="relative grid lg:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tight leading-tight">
                  {t.home.ctaTitle}
                </h2>
                <p className="mt-4 text-lg text-sky-100/90 max-w-xl leading-relaxed">
                  {t.home.ctaSubtitle}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 lg:justify-end">
                <a
                  href={quoteMailto}
                  data-testid="home-cta-consultation"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-[#0022FF] px-7 py-4 text-sm font-semibold hover:bg-sky-50 transition-colors"
                >
                  {t.home.ctaButton}
                  <ArrowRight className="h-4 w-4" />
                </a>
                <a
                  href={`tel:${CONTACT_INFO.phoneClean}`}
                  data-testid="home-cta-call"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-white/40 text-white px-7 py-4 text-sm font-semibold hover:bg-white/10 transition-colors"
                >
                  <Phone className="h-4 w-4" />
                  {CONTACT_INFO.phone}
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
